import {
  Drawer,
  Alert,
  RadioChangeEvent,
  Radio,
  Row,
  Col,
  Upload,
  Button,
  Select,
  Form,
  DatePicker,
  Input,
  UploadFile,
  notification,
  message,
} from 'antd'

import {
  DeleteFilled,
  DoubleRightOutlined,
  UploadOutlined,
} from '@ant-design/icons'

import './MaskUpdateTasksDrawer.style.less'
import { useEffect, useState } from 'react'
import FooterForm from '../../TaskTable/components/FooterForm'
import {
  TASK_STATUS,
  TASK_STATUS_UPDATER,
} from '@/screens/Order/Pages/Landing/components/ModalMassUpdate/Task.constant'
import { get, set } from 'lodash'
import moment from 'moment'
import { dateFormat } from '@/utils/date'
import { DATE_FORMAT as DATE_FORMAT_CONST } from '@/constant/date'
import {
  DATE_FORMAT,
  MAX_FILE_SIZE,
  convertToPercentageValue,
} from '../../TaskTable/TaskTable.transform'
import {
  useMassCommentTasks,
  useMaskUpdateTaskCheckUpdatable,
  useMassUploadDocumentTasks,
  useMassStatusTasks,
} from './MaskUpdateTasks.hook'
import { isEmptyValue } from '@/utils/common'
import { PlanStatuses } from '@/screens/Order/Pages/OrderOverview/components/TimeAndAction/components/DetailSection/components/TaskTemplate/Fields/StatusField/StatusField.enum'

const RadioWidth = { width: 18, marginRight: 8 }

function MaskUpdateTasksDrawer(props: any) {
  const { tasks, open = false } = props
  const { onClose, taskType, refetch } = props
  const [selectedRadio, setSelectedRadio] = useState(0)
  const [messageInfo, setMessageInfo] = useState('')
  const [formEmpty, setFormEmpty] = useState(true)

  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [form] = Form.useForm()
  const { mutate, data } = useMaskUpdateTaskCheckUpdatable()
  const {
    mutate: commentOnTasks,
    isLoading: isLoadingComment,
    isSuccess: isSuccessComment,
  } = useMassCommentTasks()
  const {
    mutate: uploadDocOnTasks,
    isLoading: isLoadingUploadDoc,
    isSuccess: isSuccessUploadDoc,
  } = useMassUploadDocumentTasks()
  const {
    mutate: uploadStatusTasks,
    isLoading: isLoadingStatusTasks,
    isSuccess: isSuccessStatusTasks,
  } = useMassStatusTasks()

  // for only update status
  const canEdit = data?.data?.can_write || false

  const options = get(
    data?.data?.role === 'updater' ? TASK_STATUS_UPDATER : TASK_STATUS,
    taskType,
    {}
  )

  const messageEditable = `You have selected ${tasks?.taskIds?.length} tasks with the same control type.`

  const messageErrorDifferentType = `You have selected ${tasks?.taskIds?.length} tasks with different control types.\n You cannot update the status.`

  const messageErrorPermission =
    "You don't have permissions to update all selected tasks anymore."
  const refresh = () => {
    form.resetFields()
    refetch()
    checkDataForSelectedForm()
  }
  useEffect(() => {
    if (isSuccessStatusTasks) {
      notification.success({
        message: 'Mass update status successfully.',
        duration: 4,
        placement: 'top',
      })
      refresh()
    }
  }, [isSuccessStatusTasks])

  const checkDataForSelectedForm = () => {
    switch (selectedRadio) {
      case 1:
        const value = form.getFieldValue('value')
        setFormEmpty(!value)
        break
      case 2:
        const comment = form.getFieldValue('comment')
        setFormEmpty(!comment)
        break
      case 3:
        setFormEmpty(fileList.length < 1)
        break
      default:
        setFormEmpty(true)
        break
    }
  }

  useEffect(() => {
    checkDataForSelectedForm()
  }, [selectedRadio, fileList])

  useEffect(() => {
    setSelectedRadio(0)
    form.resetFields()
  }, [data])

  useEffect(() => {
    if (isSuccessComment) {
      notification.success({
        message: 'Mass comment successfully.',
        duration: 4,
        placement: 'top',
      })
      refresh()
    }
  }, [isSuccessComment])

  useEffect(() => {
    if (isSuccessUploadDoc) {
      notification.success({
        message: 'Mass upload document successfully.',
        duration: 4,
        placement: 'top',
      })
      refresh()
      setFileList([])
    }
  }, [isSuccessUploadDoc])

  useEffect(() => {
    if (data?.data?.can_write) {
      setMessageInfo(messageEditable)
      return
    }
    const errorType = data?.data?.type
    if (errorType === 'type') {
      setMessageInfo(messageErrorDifferentType)
    } else {
      setMessageInfo(messageErrorPermission)
    }
  }, [data])

  useEffect(() => {
    if (!open) return
    mutate({
      tasks: tasks?.taskIds,
    })
  }, [tasks, open])

  const uploadProps = {
    accept:
      '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,image/*',
    onRemove: (file) => {
      const index = fileList.findIndex((item) => item.uid === file.uid)
      const newFileList = fileList.slice()
      if (index !== -1) {
        newFileList.splice(index, 1)
      }
      setFileList(newFileList)
    },
    onChange: (e) => {
      if (e.fileList.length) {
        window.onbeforeunload = () => 'warning'
      } else {
        window.onbeforeunload = undefined
      }
    },
    beforeUpload: (file) => {
      const isLt20M = file.size <= MAX_FILE_SIZE
      if (!isLt20M) {
        message.warn('Can not upload file that is more than 20mb')
        return false
      }

      if (
        !(
          file.type.startsWith('image/') ||
          file.type !== 'application/msword' ||
          file.type !==
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          file.type !== 'application/vnd.ms-excel' ||
          file.type !==
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          file.type !== 'application/pdf'
        )
      ) {
        message.error(`${file.name} is not an Excel, Word, PDF, or Image file`)
        return false
      }

      setFileList([file])
      setFormEmpty(false)
      return false
    },
    showUploadList: {
      showRemoveIcon: true,
      removeIcon: <DeleteFilled />,
    },
    fileList: fileList,
  }

  const onChange = (e: RadioChangeEvent) => {
    setSelectedRadio(e.target.value)
  }

  const onUpdateStatusPlan = async (newStatus: string, actualDate: any) => {
    let task = {}
    let status: any = newStatus

    if (isEmptyValue(status)) {
      if (taskType === newStatus) {
        set(task, 'value', 'planned')
        if (taskType !== 'percentage_progress_type')
          set(task, 'activity_value', 'planned')
        else set(task, 'activity_value', 'planned')
      } else if (
        status === PlanStatuses.APPROVED ||
        status === PlanStatuses.DONE ||
        status === PlanStatuses.PASS ||
        status === PlanStatuses.FAIL ||
        status === PlanStatuses.NOT_APPLICABLE
      ) {
        set(task, 'progress_status', 'done')
      } else {
        set(task, 'progress_status', 'in_progress')
      }
    } else {
      if (status === 'planned') {
        set(task, 'progress_status', 'in_progress')
      } else if (
        parseInt(status) === 100 ||
        status === PlanStatuses.APPROVED ||
        status === PlanStatuses.DONE ||
        status === PlanStatuses.PASS ||
        status === PlanStatuses.FAIL ||
        status === PlanStatuses.NOT_APPLICABLE
      ) {
        set(task, 'progress_status', 'done')
      } else {
        set(task, 'progress_status', 'in_progress')
      }
    }

    if (status === 'planned') set(task, 'value', 'planned')
    else set(task, 'value', `${status}`)
    set(task, 'json_value.current_status', status)
    set(task, 'activity_value', status)
    set(
      task,
      'actual_date',
      dateFormat(
        actualDate?.toISOString?.() || actualDate,
        DATE_FORMAT_CONST.YYYYMMDD
      )
    )
    return { ...task, type: 'status' }
  }

  const onFinish = async (values) => {
    switch (selectedRadio) {
      case 1:
        const payload = await onUpdateStatusPlan(
          values.value,
          values.actual_date
        )
        uploadStatusTasks({
          tasks: tasks?.taskIds,
          progress_status: payload['progress_status'],
          actual_date: payload['actual_date'] || null,
          value: `${values.value}`,
        })
        break
      case 2:
        commentOnTasks({
          tasks: tasks?.taskIds,
          comment: values?.comment,
        })
        break
      case 3:
        await uploadDocOnTasks({
          fileList,
          tasks: tasks?.taskIds,
        })
        break
      default:
        break
    }
  }

  const transformedOptions = Object.entries(options).map(([key, value]) => {
    return {
      value: value,
      label: key,
    }
  })

  function RadioLabel(props: any) {
    const { value, label, disabled = false, customClassLabel } = props
    var className = 'form-item-label'
    if (disabled) {
      className = className + customClassLabel
    }
    return (
      <Radio
        checked={value === selectedRadio}
        onChange={onChange}
        value={value}
        disabled={disabled}
      >
        <span className={className}>{label}</span>
      </Radio>
    )
  }
  const handleChangePerchentage = (event) => {
    let newValue = event.target.value
    form.setFieldValue('value', convertToPercentageValue(newValue))
  }

  function StatusField() {
    var customClassLabel = ''
    if (!data?.data?.can_write) {
      customClassLabel = ' text-disabled'
    }
    if (taskType === 'percentage_progress_type') {
      return (
        <Col span={24}>
          <Form.Item
            label={
              <RadioLabel disabled={!canEdit} label={'Status'} value={1} />
            }
            name="value"
          >
            <Input
              onChange={handleChangePerchentage}
              disabled={selectedRadio !== 1}
              suffix={'%'}
              autoFocus
            />
          </Form.Item>
        </Col>
      )
    }
    return (
      <Col span={24}>
        <Form.Item
          label={
            <RadioLabel
              customClassLabel={customClassLabel}
              disabled={!canEdit}
              label="Status"
              value={1}
            />
          }
          name="value"
        >
          <Select
            placeholder="Select"
            size="middle"
            disabled={selectedRadio !== 1}
            options={transformedOptions}
          />
        </Form.Item>
        <Form.Item
          label={
            <>
              <span style={RadioWidth}></span>
              <span className={'form-item-label ' + customClassLabel}>
                Actual Date
              </span>
            </>
          }
          name="actual_date"
        >
          <DatePicker
            placeholder="Date"
            size="middle"
            format={DATE_FORMAT}
            disabled={selectedRadio !== 1}
            defaultValue={moment(new Date(), DATE_FORMAT)}
          />
        </Form.Item>
      </Col>
    )
  }

  return (
    <Drawer
      title=""
      closeIcon={<DoubleRightOutlined />}
      placement="right"
      onClose={onClose}
      open={open}
      width={550}
      mask={false}
    >
      <Form
        form={form}
        onFinish={onFinish}
        layout="horizontal"
        labelCol={{ span: 9 }}
        wrapperCol={{ span: 15 }}
        colon={false}
        onValuesChange={checkDataForSelectedForm}
        initialValues={{ actual_date: moment(new Date(), DATE_FORMAT) }}
      >
        <div className=" task-form mask-update-container">
          <span className="order-no">
            <b>Mass Update</b>
          </span>
          <br />
          <br />
          <Alert
            message={messageInfo}
            type={canEdit ? 'success' : 'warning'}
            showIcon
          />
          <br />
          <span className="sub-label">
            Check the boxes of the fields to update and set up new values:
          </span>
          <br />
          <br />
          <Row gutter={[16, 16]}>
            <StatusField />
            <Col span={24}>
              <Form.Item
                label={
                  <RadioLabel placeholder="Date" label={'Comment'} value={2} />
                }
                name="comment"
              >
                <Input.TextArea
                  placeholder="Text"
                  disabled={selectedRadio !== 2}
                  rows={1}
                  autoFocus
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<RadioLabel label={'Document'} value={3} />}
                name="document"
              >
                <Upload {...uploadProps}>
                  <Button
                    disabled={selectedRadio !== 3}
                    icon={<UploadOutlined />}
                  >
                    Upload File
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
          <br />

          <FooterForm
            isLoading={
              isLoadingComment || isLoadingUploadDoc || isLoadingStatusTasks
            }
            handleCancel={onClose}
            saveText="Save"
            disableSaveButton={formEmpty}
          />
        </div>
      </Form>
    </Drawer>
  )
}

export default MaskUpdateTasksDrawer
